import { useMemo, useRef, useState } from "react";
import type { CSSProperties, Dispatch, RefObject, SetStateAction } from "react";
import { Info, LogIn, LogOut, Timer, MoonStar, CalendarClock } from "lucide-react";
import { useToast } from "../../../context/ToastContext.js";
import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { cn } from "../../../lib/utils.js";
import { Field, CollapsibleSection } from "../sectionShared.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Badge } from "../../ui/badge.js";
import { Input } from "../../ui/input.js";
import { SummaryStatCard, SummaryStatSuffix } from "../../SummaryStatCard.js";

export interface ShiftRecord {
  id: string; shiftType: string; shiftStartHHMM: string; shiftEndHHMM: string;
  effectiveFrom: string; setById: string; setAt: string; reason: string | null;
  /** Resolved from setById by the current-shift endpoint, so the UI can name whoever
   *  assigned the shift. Absent on the history list, which does not resolve them. */
  setByRole?: string | null;
  setByName?: string | null;
  /** Set by the current-shift endpoint while an approved request supplies these
   *  hours instead of the roster's. The roster applies again once it ends. */
  temporary?: {
    requestId: string;
    startDate: string;
    endDate: string;
    approverId: string | null;
    remark: string;
  } | null;
}

type ShiftTypeValue = "MORNING" | "EVENING" | "NIGHT" | "CUSTOM";

/** A full working day. Matches DEFAULT_SHIFT_HOURS on the API. */
export const SHIFT_DURATION_HOURS = 9;
/** The window snaps to half hours, so 08:30 / 15:30 starts are selectable. */
const STEP = 0.5;

/**
 * Start hours that have a name of their own; every other start is CUSTOM.
 *
 * No longer a row of buttons -- the timeline sets the window directly, and three
 * presets above it were a second way to do the same thing. The classification is
 * still stored and shown in the history, so the lookup stays.
 */
const SHIFT_TYPE_BY_HOUR = new Map<number, ShiftTypeValue>([
  [9, "MORNING"], [17, "EVENING"], [22, "NIGHT"],
]);

const pad = (n: number) => String(n).padStart(2, "0");

/** Snap an hour-of-day to the nearest half hour, wrapped into 0–24. */
function snapHour(h: number): number {
  const s = Math.round(h / STEP) * STEP;
  return ((s % 24) + 24) % 24;
}

/** 8.5 -> "08:30" */
function fmtHour(h: number): string {
  const mins = Math.round(h * 60) % 1440;
  return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
}

/** Static star positions — arbitrary Tailwind values, nothing runtime here. */
const STARS = [
  "left-[8%] top-[15px] size-[3px]",
  "left-[12%] top-[31px] size-[2px] [animation-delay:0.8s]",
  "left-[5.5%] top-[39px] size-[2px] [animation-delay:1.5s]",
  "left-[90%] top-[12px] size-[3px] [animation-delay:0.4s]",
  "left-[95%] top-[28px] size-[2px] [animation-delay:1.2s]",
  "left-[86.5%] top-[40px] size-[2px] [animation-delay:2s]",
];

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canEditShift: boolean;
  employeeId: string | null;
  ensureEmployee: () => Promise<string | null>;
  currentShift: ShiftRecord | null; setCurrentShift: (v: ShiftRecord | null) => void;
  shiftHistory: ShiftRecord[]; setShiftHistory: (v: ShiftRecord[]) => void;
  shiftStartHour: number; setShiftStartHour: (v: number) => void;
  shiftType: ShiftTypeValue; setShiftType: (v: ShiftTypeValue) => void;
  shiftEffFrom: string; setShiftEffFrom: (v: string) => void;
  shiftReason: string; setShiftReason: (v: string) => void;
  shiftSaving: boolean; setShiftSaving: Dispatch<SetStateAction<boolean>>;
  shiftError: string | null; setShiftError: (v: string | null) => void;
  shiftHistExpanded: boolean; setShiftHistExpanded: Dispatch<SetStateAction<boolean>>;
  shiftDragRef: RefObject<HTMLDivElement>;
}

export function WorkScheduleSection({
  sectionRef, canEditShift, employeeId, ensureEmployee,
  currentShift, setCurrentShift, shiftHistory, setShiftHistory,
  shiftStartHour, setShiftStartHour, shiftType, setShiftType,
  shiftEffFrom, setShiftEffFrom, shiftReason, setShiftReason,
  shiftSaving, setShiftSaving, shiftError, setShiftError,
  shiftHistExpanded, setShiftHistExpanded, shiftDragRef,
}: Props) {
  const toast = useToast();
  // Where the current time sits on the 24-hour strip.
  const nowPct = useMemo(() => {
    const n = new Date();
    return ((n.getHours() + n.getMinutes() / 60) / 24) * 100;
  }, []);

  const grabOffset = useRef(0);   // grab point inside the window, in hours
  const [dragging, setDragging] = useState(false);

  const start    = shiftStartHour;
  const endHour  = (start + SHIFT_DURATION_HOURS) % 24;
  const wraps    = start + SHIFT_DURATION_HOURS > 24;
  const headSpan = Math.min(SHIFT_DURATION_HOURS, 24 - start);          // before midnight
  const tailSpan = wraps ? start + SHIFT_DURATION_HOURS - 24 : 0;       // after midnight
  const startHHMM = fmtHour(start);
  const endHHMM   = fmtHour(endHour);

  if (!(canEditShift || currentShift)) {
    return (
      <CollapsibleSection id="schedule" sectionRef={sectionRef} title="Work Schedule" editableWhenReadOnly={canEditShift}>
        <div className="py-8 text-center text-[13px] text-surface-400">No shift assigned</div>
      </CollapsibleSection>
    );
  }

  function applyStart(h: number) {
    const snapped = snapHour(h);
    setShiftStartHour(snapped);
    setShiftType(SHIFT_TYPE_BY_HOUR.get(snapped) ?? "CUSTOM");
  }

  async function saveShift() {
    setShiftSaving(true);
    const eid = employeeId ?? await ensureEmployee();
    if (!eid) {
      setShiftError("Fill in First Name and Email above before assigning a shift.");
      setShiftSaving(false);
      throw new Error("validation");
    }
    try {
      await api.post(`/employees/${eid}/shift`, {
        shiftType: shiftType, shiftStartHHMM: startHHMM, shiftEndHHMM: endHHMM,
        effectiveFrom: shiftEffFrom,
        reason: shiftReason || undefined,
      });
      const [curr, hist] = await Promise.all([
        api.get<ShiftRecord | null>(`/employees/${eid}/shift/current`),
        api.get<ShiftRecord[]>(`/employees/${eid}/shift/history`),
      ]);
      setCurrentShift(curr.data);
      setShiftHistory(hist.data);
      setShiftReason("");
    } catch (err) {
      logError("EmployeeProfilePage.saveShift", err, {
        employeeId, shiftType, startHHMM, effectiveFrom: shiftEffFrom,
      });
      toast.show(err instanceof Error ? err.message : "Failed to save shift", "error");
      throw err;
    } finally { setShiftSaving(false); }
  }

  /* ── dragging the window (pointer events: mouse, touch and pen) ────────── */

  function hourAt(clientX: number): number {
    const rect = shiftDragRef.current?.getBoundingClientRect();
    if (!rect) return start;
    return Math.min(24, Math.max(0, ((clientX - rect.left) / rect.width) * 24));
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!canEditShift) return;
    const h = hourAt(e.clientX);
    const rel = (((h - start) % 24) + 24) % 24;
    // Grabbing inside the window keeps the grab point; tapping the bare track
    // centres the window on the tap instead.
    grabOffset.current = rel <= SHIFT_DURATION_HOURS ? rel : SHIFT_DURATION_HOURS / 2;
    if (rel > SHIFT_DURATION_HOURS) applyStart(h - SHIFT_DURATION_HOURS / 2);
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!canEditShift || !dragging) return;
    applyStart(hourAt(e.clientX) - grabOffset.current);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (!canEditShift) return;
    if (e.key === "ArrowRight") { e.preventDefault(); applyStart(start + STEP); }
    if (e.key === "ArrowLeft")  { e.preventDefault(); applyStart(start - STEP); }
  }

  const windowLabel = (
    <span className="relative inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[13px] font-semibold">
      {startHHMM} <span aria-hidden="true">→</span> {endHHMM}
      {wraps && <sup className="rounded bg-white/25 px-1 py-px text-[9px] font-bold">+1</sup>}
    </span>
  );

  const effLabel = shiftEffFrom
    ? new Date(`${shiftEffFrom}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "the selected date";

  return (
    <CollapsibleSection
      id="schedule"
      sectionRef={sectionRef}
      // Stays interactive on a read-only profile when the viewer may set shifts: a
      // manager opens a team member's profile view-only but still owns their roster.
      editableWhenReadOnly={canEditShift}
      title={
        <span className="flex w-full items-center justify-between gap-3">
          Work Schedule
          <Badge tone={currentShift ? "success" : "neutral"}>
            <span className={cn("size-1.5 rounded-full", currentShift ? "bg-[var(--color-success-fg)]" : "bg-surface-400")} />
            {currentShift ? "Assigned" : "Unassigned"}
          </Badge>
        </span>
      }
    >

      <p className="m-0 mb-2.5 flex items-center gap-1.5 text-[12px] text-surface-500">
        <Info size={13} className="shrink-0" />
        {currentShift
          ? <>Current <span className="font-mono font-semibold text-surface-700">{currentShift.shiftStartHHMM}&ndash;{currentShift.shiftEndHHMM}</span>; changes apply from the effective date</>
          : "No shift assigned — attendance uses the office default threshold"}
      </p>

      {/* 24-hour timeline */}
      <div className="flex flex-col gap-1.5">
        <div className="flex justify-between px-px font-mono text-[10px] font-medium text-surface-400" aria-hidden="true">
          {["00", "03", "06", "09", "12", "15", "18", "21", "24"].map(h => <span key={h}>{h}</span>)}
        </div>

        <div
          ref={shiftDragRef}
          className="shift-track"
          {...(dragging ? { "data-drag": "true" } : {})}
          {...(canEditShift ? {} : { "data-readonly": "true" })}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="shift-grid" aria-hidden="true">
            {Array.from({ length: 24 }, (_, i) => (
              <span key={i} className={i % 3 === 0 ? "is-major" : undefined} />
            ))}
          </div>

          <svg className="shift-sunpath" viewBox="0 0 240 84" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 70 C 40 70, 60 14, 120 14 S 200 70, 240 70" vectorEffect="non-scaling-stroke" />
          </svg>
          <span className="shift-sun" aria-hidden="true" />
          <MoonStar size={13} className="shift-moon" aria-hidden="true" />
          {STARS.map(cls => <span key={cls} className={cn("shift-star", cls)} aria-hidden="true" />)}

          <div className="shift-phases" aria-hidden="true">
            <span className="is-night">Night</span>
            <span className="is-dawn">Dawn</span>
            <span className="is-day">Day</span>
            <span className="is-dusk">Dusk</span>
            <span className="is-night">Night</span>
          </div>

          {/* Current time — a live clock reading, so it can only be a custom property. */}
          <div className="shift-now" style={{ "--sw-now": `${nowPct}%` } as CSSProperties} aria-hidden="true" />

          {/* The window before midnight. Its geometry follows the drag, so left/width
              are custom properties rather than static classes. */}
          <button
            type="button"
            className="shift-window"
            disabled={!canEditShift}
            aria-label={`Shift window ${startHHMM} to ${endHHMM}. Use the arrow keys to move it by 30 minutes.`}
            onKeyDown={handleKeyDown}
            {...(wraps ? { "data-wrap": "head" } : {})}
            style={{ "--sw-left": `${(start / 24) * 100}%`, "--sw-width": `${(headSpan / 24) * 100}%` } as CSSProperties}
          >
            <span className="shift-sheen" aria-hidden="true" />
            <span className="shift-grip is-left" aria-hidden="true" />
            {!wraps && <span className="shift-grip is-right" aria-hidden="true" />}
            {headSpan >= tailSpan && windowLabel}
          </button>

          {/* The part that spills past midnight, drawn back at 00:00. */}
          {wraps && (
            <div
              className="shift-window"
              data-wrap="tail"
              aria-hidden="true"
              style={{ "--sw-left": "0%", "--sw-width": `${(tailSpan / 24) * 100}%` } as CSSProperties}
            >
              <span className="shift-grip is-right" />
              {tailSpan > headSpan && windowLabel}
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-surface-500">
          {canEditShift
            ? `Drag the window or tap the timeline · ${SHIFT_DURATION_HOURS}-hour window · arrow keys nudge 30 min`
            : `${SHIFT_DURATION_HOURS}-hour window`}
        </p>
      </div>

      {/* Summary -- the tiles' own compact "row" layout rather than the tall
          stacked one: four read-only figures do not need a quarter of the screen. */}
      <div className="mt-3 flex flex-wrap gap-2">
        <SummaryStatCard
          layout="row" icon={LogIn} tone="brand" label="Clock in"
          value={startHHMM} valueClassName="font-mono"
          className="flex-[1_1_150px]"
        />
        <SummaryStatCard
          layout="row" icon={LogOut} tone="brand" label="Clock out"
          // The day marker rides after the time rather than on a line of its own:
          // "02:00 next day" is one fact, and the tile keeps the others' height.
          value={<>{endHHMM}<SummaryStatSuffix> {wraps ? "next day" : "same day"}</SummaryStatSuffix></>}
          valueClassName="font-mono"
          className="flex-[1_1_170px]"
        />
        <SummaryStatCard
          layout="row" icon={Timer} tone="info" label="Duration"
          value={`${SHIFT_DURATION_HOURS}h 00m`} valueClassName="font-mono"
          className="flex-[1_1_150px]"
        />
        <SummaryStatCard
          layout="row" icon={MoonStar} tone={wraps ? "accent" : "success"} label="Midnight"
          value={wraps ? "Crosses midnight" : "Within the day"} valueClassName="text-[13px]"
          className="flex-[1_1_170px]"
        />
      </div>

      {canEditShift && (
        <>
          {/* Effective from + reason */}
          <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <Field label="Effective from">
              <Input type="date" value={shiftEffFrom} onChange={e => setShiftEffFrom(e.target.value)} />
            </Field>
            <Field label={<>Reason <span className="font-medium text-surface-400">(optional)</span></>}>
              <Input value={shiftReason} onChange={e => setShiftReason(e.target.value)} placeholder="e.g. Client project night shift" />
            </Field>
          </div>

          {/* Validation only — API failures surface in the global toaster. */}
          {shiftError && <div className="mt-2 text-[12px] text-[var(--color-error)]">{shiftError}</div>}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12px] text-surface-500">
              Applies to attendance from <strong className="font-semibold text-surface-800">{effLabel}</strong>.
            </p>
            <AsyncButton
              onClick={() => { setShiftError(null); return saveShift(); }}
              confirm={{
                title: currentShift ? "Update this shift?" : "Assign this shift?",
                description: currentShift
                  ? "Replaces the employee's current shift from the Effective From date. The previous shift stays in the history below."
                  : "Assigns this shift to the employee from the Effective From date.",
                actionText: currentShift ? "Update Shift" : "Assign Shift",
              }}
              disabled={shiftSaving}
            >
              <CalendarClock size={14} /> {currentShift ? "Update shift" : "Assign shift"}
            </AsyncButton>
          </div>
        </>
      )}

      {/* History accordion */}
      {shiftHistory.length > 1 && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--color-surface-100)", paddingTop: 12 }}>
          <button onClick={() => setShiftHistExpanded(x => !x)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--color-surface-500)", fontWeight: 600 }}>
            {shiftHistExpanded ? "▲" : "▼"} Shift History ({shiftHistory.length} entries)
          </button>
          {shiftHistExpanded && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 }}>
              <thead>
                <tr style={{ background: "var(--color-surface-50)" }}>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "var(--color-surface-500)" }}>Effective</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "var(--color-surface-500)" }}>Shift</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "var(--color-surface-500)" }}>Window</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "var(--color-surface-500)" }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {shiftHistory.map(s => (
                  <tr key={s.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                    <td style={{ padding: "6px 10px", fontVariantNumeric: "tabular-nums" }}>{new Date(s.effectiveFrom).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}</td>
                    <td style={{ padding: "6px 10px" }}><span style={{ fontSize: 10, background: "var(--color-info-bg)", color: "var(--color-info-fg-strong)", padding: "2px 7px", borderRadius: 999 }}>{s.shiftType}</span></td>
                    <td style={{ padding: "6px 10px", fontVariantNumeric: "tabular-nums", fontFamily: "monospace", fontSize: 11 }}>{s.shiftStartHHMM} → {s.shiftEndHHMM}</td>
                    <td style={{ padding: "6px 10px", color: "var(--color-surface-500)" }}>{s.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
